import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Download, User, Filter, Edit2, Save, X, CheckCircle, Upload } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { useNotification } from '@/hooks/useNotification';
import { invoiceService } from '@/services/invoiceService';
import { UserService } from '@/services/userService';
import { 
  Invoice, 
  InvoiceListParams, 
  HonorariumCalculation 
} from '@/types/invoice';

// Helper function to get current month in YYYY-MM format
const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

interface BulkEditValues {
  exceptions: string;
  remarks: string;
  fixedMonthlyAmount: string;
}

export const PnCReview = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedProgram, setSelectedProgram] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedInvoices, setSelectedInvoices] = useState<number[]>([]);
  // Bulk edit state (up to BULK_EDIT_LIMIT rows at a time)
  const [bulkEditMap, setBulkEditMap] = useState<Map<number, BulkEditValues>>(new Map());
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [saveErrors, setSaveErrors] = useState<Map<number, string>>(new Map());
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [showSalaryImport, setShowSalaryImport] = useState(false);
  const [importedSalaryData, setImportedSalaryData] = useState<Map<string, number>>(new Map());
  const [showExceptionsImport, setShowExceptionsImport] = useState(false);
  const [importedExceptionsData, setImportedExceptionsData] = useState<Map<string, number>>(new Map());
  const fileInputRef = useRef<HTMLInputElement>(null);
  const exceptionsFileInputRef = useRef<HTMLInputElement>(null);
  
  const { showSuccess, showError } = useNotification();
  const userService = new UserService();
  
  // API query filters
  const [filters, setFilters] = useState<InvoiceListParams>({
    page: 1,
    page_size: 10,
    month_year: getCurrentMonth()
  });

  const programs = [
    { value: '', label: 'All Programs' },
    { value: 'TBTP', label: 'The Billion Tree Project' },
    { value: 'WM', label: 'Waste Management' },
    { value: 'WE', label: 'Women Equity' },
    { value: 'LL', label: 'Learn Labs' },
    { value: 'SD', label: 'Sports for Development' },
    { value: 'RF', label: 'Regenerative Farming' },
    { value: 'YOGA', label: 'Yoga' },
  ];

  // Fetch application roles using React Query
  const {
    data: rolesData,
    isLoading: rolesLoading
  } = useQuery({
    queryKey: ['application-roles'],
    queryFn: () => userService.getApplicationRoles(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Prepare role options for dropdown
  const roleOptions = [
    { value: '', label: 'All Roles' },
    ...(rolesData?.roles || []).map((role, index) => {
      // Handle the actual API response format: {id, title, description}
      if (typeof role === 'string') {
        return { value: role, label: role };
      } else {
        return {
          value: role.id || `role-${index}`,
          label: role.title || role.id || `Role ${index + 1}`
        };
      }
    })
  ];

  const stateOptions = [
    { value: '', label: 'All States' },
    { value: 'Draft', label: 'Draft' },
    { value: 'Approved By PnC', label: 'Approved By PnC' },
    { value: 'Approved By BU', label: 'Approved By BU' },
  ];

  const pageSizeOptions = [
    { value: 10, label: '10 per page' },
    { value: 50, label: '50 per page' },
    { value: 100, label: '100 per page' },
  ];
  const BULK_EDIT_LIMIT = 100;

  // Update filters when search, page, program, role, state, or page size changes
  useEffect(() => {
    const newFilters = {
      page: currentPage,
      page_size: pageSize,
      q: searchQuery?.trim() || undefined,
      program: selectedProgram || undefined,
      role: selectedRole || undefined,
      state: selectedState || undefined,  // Use 'state' parameter with original values
      month_year: selectedMonth || undefined
    };
    
    setFilters(newFilters);
  }, [searchQuery, currentPage, selectedProgram, selectedRole, selectedState, selectedMonth, pageSize]);

  // Fetch invoices using React Query - PnC section can see Draft invoices
  const {
    data: invoicesData,
    isLoading,
    error,
    refetch
  } = useQuery({
    queryKey: ['pnc-invoices', filters],
    queryFn: () => invoiceService.getInvoices('pnc', filters),
    placeholderData: (previousData) => previousData,
  });

  const confirmIfEditing = (action: () => void) => {
    if (isBulkEditMode) {
      if (window.confirm('You have unsaved edits. Changing filters will discard them. Continue?')) {
        cancelAllEditing();
        action();
      }
    } else {
      action();
    }
  };

  const handleSearch = (value: string) => {
    confirmIfEditing(() => { setSearchQuery(value); setCurrentPage(1); });
  };

  const handlePageSizeChange = (size: number) => {
    confirmIfEditing(() => { setPageSize(size); setCurrentPage(1); });
  };

  const handleExportData = async () => {
    try {
      const filename = `pnc-invoices-${selectedMonth}-${new Date().toISOString().split('T')[0]}.csv`;
      // Only pass month_year filter for export, remove pagination
      const exportFilters = {
        month_year: selectedMonth || undefined
      };
      await invoiceService.exportInvoices('pnc', exportFilters, filename);
      showSuccess('Export Data', 'PnC invoice data exported successfully.');
    } catch (error) {
      showError('Export Failed', 'Failed to export PnC data. Please try again.');
    }
  };

  // Derived bulk-edit helpers
  const isBulkEditMode = bulkEditMap.size > 0;
  const canAddMoreEdits = bulkEditMap.size < BULK_EDIT_LIMIT;

  const startEditing = (invoice: Invoice) => {
    if (bulkEditMap.size >= BULK_EDIT_LIMIT) {
      showError('Limit Reached', `You can only bulk edit up to ${BULK_EDIT_LIMIT} invoices at a time.`);
      return;
    }
    if (bulkEditMap.has(invoice.id)) return;

    const importedAmount = importedSalaryData.get(invoice.employee_id);
    const importedExceptions = importedExceptionsData.get(invoice.employee_id);

    const values: BulkEditValues = {
      exceptions: importedExceptions?.toString() || invoice.exceptions?.toString() || '',
      remarks: invoice.pnc_remarks || '',
      fixedMonthlyAmount: importedAmount?.toString() || ''
    };

    setBulkEditMap(prev => new Map(prev).set(invoice.id, values));
    setSaveErrors(prev => { const n = new Map(prev); n.delete(invoice.id); return n; });
  };

  const startBulkEditing = () => {
    if (selectedInvoices.length === 0) {
      showError('No Selection', 'Please select Draft invoices to edit.');
      return;
    }
    const draftSelected = selectedInvoices.filter(id => draftInvoices.find(inv => inv.id === id));
    if (draftSelected.length > BULK_EDIT_LIMIT) {
      showError('Limit Exceeded', `You selected ${draftSelected.length} invoices. Maximum for bulk edit is ${BULK_EDIT_LIMIT}.`);
      return;
    }

    const newMap = new Map(bulkEditMap);
    draftSelected.forEach(id => {
      if (newMap.has(id)) return;
      const invoice = invoices.find(inv => inv.id === id);
      if (!invoice) return;
      newMap.set(invoice.id, {
        exceptions: importedExceptionsData.get(invoice.employee_id)?.toString() || invoice.exceptions?.toString() || '',
        remarks: invoice.pnc_remarks || '',
        fixedMonthlyAmount: importedSalaryData.get(invoice.employee_id)?.toString() || ''
      });
    });

    if (newMap.size > BULK_EDIT_LIMIT) {
      showError('Limit Exceeded', `Total editable rows would exceed ${BULK_EDIT_LIMIT}. Please reduce your selection.`);
      return;
    }
    setBulkEditMap(newMap);
    setSaveErrors(new Map());
  };

  const updateEditValues = (invoiceId: number, field: keyof BulkEditValues, value: string) => {
    setBulkEditMap(prev => {
      const next = new Map(prev);
      const current = next.get(invoiceId);
      if (current) next.set(invoiceId, { ...current, [field]: value });
      return next;
    });
  };

  const cancelRowEditing = (invoiceId: number) => {
    setBulkEditMap(prev => { const n = new Map(prev); n.delete(invoiceId); return n; });
    setSaveErrors(prev => { const n = new Map(prev); n.delete(invoiceId); return n; });
  };

  const cancelAllEditing = () => {
    setBulkEditMap(new Map());
    setSaveErrors(new Map());
    setSavingIds(new Set());
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      showError('Invalid File', 'Please upload a CSV file only.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target?.result as string;
        const lines = csvText.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          showError('Invalid CSV', 'CSV must have at least a header row and one data row.');
          return;
        }

        const salaryMap = new Map<string, number>();
        let errorCount = 0;
        let successCount = 0;

        // Skip header row and process data rows
        for (let i = 1; i < lines.length; i++) {
          const [employeeId, salary] = lines[i].split(',').map(cell => cell.trim());
          
          if (!employeeId || !salary) {
            errorCount++;
            continue;
          }

          const salaryValue = parseFloat(salary);
          if (isNaN(salaryValue) || salaryValue <= 0) {
            errorCount++;
            continue;
          }

          salaryMap.set(employeeId, salaryValue);
          successCount++;
        }

        if (successCount === 0) {
          showError('Import Failed', 'No valid monthly amount data found in the CSV file.');
          return;
        }

        setImportedSalaryData(salaryMap);
        showSuccess(
          'Monthly Amount Data Imported', 
          `Successfully imported ${successCount} monthly amount records.${errorCount > 0 ? ` ${errorCount} rows had errors and were skipped.` : ''}`
        );
        setShowSalaryImport(false);

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }

      } catch (error) {
        showError('Import Failed', 'Failed to parse CSV file. Please check the format.');
      }
    };

    reader.readAsText(file);
  };

  const handleExceptionsFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      showError('Invalid File', 'Please upload a CSV file only.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const csvText = e.target?.result as string;
        const lines = csvText.split('\n').filter(line => line.trim());
        
        if (lines.length < 2) {
          showError('Invalid CSV', 'CSV must have at least a header row and one data row.');
          return;
        }

        const exceptionsMap = new Map<string, number>();
        let errorCount = 0;
        let successCount = 0;

        // Skip header row and process data rows
        for (let i = 1; i < lines.length; i++) {
          const [employeeId, exceptions] = lines[i].split(',').map(cell => cell.trim());
          
          if (!employeeId || !exceptions) {
            errorCount++;
            continue;
          }

          const exceptionsValue = parseInt(exceptions);
          if (isNaN(exceptionsValue) || exceptionsValue < 0) {
            errorCount++;
            continue;
          }

          exceptionsMap.set(employeeId, exceptionsValue);
          successCount++;
        }

        if (successCount === 0) {
          showError('Import Failed', 'No valid exceptions data found in the CSV file.');
          return;
        }

        setImportedExceptionsData(exceptionsMap);
        showSuccess(
          'Exceptions Data Imported', 
          `Successfully imported ${successCount} exceptions records.${errorCount > 0 ? ` ${errorCount} rows had errors and were skipped.` : ''}`
        );
        setShowExceptionsImport(false);

        // Reset file input
        if (exceptionsFileInputRef.current) {
          exceptionsFileInputRef.current.value = '';
        }

      } catch (error) {
        showError('Import Failed', 'Failed to parse CSV file. Please check the format.');
      }
    };

    reader.readAsText(file);
  };

  const applySalaryData = () => {
    if (importedSalaryData.size === 0) {
      showError('No Data', 'No monthly amount data available to apply. Please import CSV first.');
      return;
    }

    let appliedCount = 0;
    const currentInvoices = invoicesData?.list || [];
    
    currentInvoices.forEach(invoice => {
      if (importedSalaryData.has(invoice.employee_id)) {
        // This will trigger a refetch, but for immediate UI feedback we could update local state
        appliedCount++;
      }
    });

    if (appliedCount === 0) {
      showError('No Matches', 'No matching employee IDs found between CSV and current invoices.');
    } else {
      showSuccess('Applied Monthly Amount Data', `Monthly amount data ready for ${appliedCount} employees. Edit individual rows to apply the imported values.`);
    }
  };

  const saveRow = async (invoiceId: number): Promise<boolean> => {
    const editVals = bulkEditMap.get(invoiceId);
    if (!editVals) return false;
    const invoice = invoices.find(inv => inv.id === invoiceId);
    if (!invoice) return false;

    try {
      setSavingIds(prev => new Set(prev).add(invoiceId));
      setSaveErrors(prev => { const n = new Map(prev); n.delete(invoiceId); return n; });

      const exceptionsVal = parseInt(editVals.exceptions);
      const fixedAmount = parseFloat(editVals.fixedMonthlyAmount);
      const hasFixedAmount = !isNaN(fixedAmount) && fixedAmount > 0;
      let calculatedAmount: number | undefined;

      if (hasFixedAmount) {
        const updatedInvoice = {
          ...invoice,
          exceptions: !isNaN(exceptionsVal) ? exceptionsVal : (invoice.exceptions || 0),
          invoice_value: fixedAmount,
        };
        calculatedAmount = calculateSalary(updatedInvoice);
      }

      const payload: Record<string, any> = { id: invoiceId };
      if (!isNaN(exceptionsVal)) payload.exception = exceptionsVal;
      if (editVals.remarks !== undefined && editVals.remarks !== (invoice.pnc_remarks || '')) payload.pnc_remarks = editVals.remarks;
      if (calculatedAmount !== undefined && calculatedAmount > 0) payload.invoice_value = calculatedAmount;

      await invoiceService.updateInvoice('pnc', payload as any);

      setBulkEditMap(prev => { const n = new Map(prev); n.delete(invoiceId); return n; });
      return true;
    } catch (error: any) {
      const msg = error?.response?.data?.message || error?.message || 'Failed to update invoice.';
      setSaveErrors(prev => new Map(prev).set(invoiceId, msg));
      return false;
    } finally {
      setSavingIds(prev => { const n = new Set(prev); n.delete(invoiceId); return n; });
    }
  };

  const saveAllChanges = async () => {
    if (bulkEditMap.size === 0) return;
    setIsBulkSaving(true);
    setSaveErrors(new Map());

    try {
      const ids = Array.from(bulkEditMap.keys());
      const results = await Promise.allSettled(ids.map(id => saveRow(id)));
      const successCount = results.filter(r => r.status === 'fulfilled' && r.value === true).length;
      const failCount = ids.length - successCount;

      if (failCount === 0) {
        showSuccess('All Saved', `Successfully updated ${successCount} invoice(s).`);
      } else if (successCount === 0) {
        showError('Save Failed', `All ${failCount} invoice(s) failed to save.`);
      } else {
        showSuccess('Partial Save', `${successCount} saved, ${failCount} failed. Review highlighted rows.`);
      }

      refetch();
    } catch (error: any) {
      showError('Save Error', error?.message || 'An unexpected error occurred during bulk save.');
    } finally {
      setIsBulkSaving(false);
    }
  };

  const lockInvoices = async (invoiceIds: number[]) => {
    try {
      const lockRequests = invoiceIds.map(id => ({ id }));
      await invoiceService.lockInvoices('pnc', lockRequests);
      
      showSuccess('Invoices Locked', `${invoiceIds.length} invoice(s) locked successfully and sent to BU for review.`);
      refetch();
      setSelectedInvoices([]);
    } catch (error: any) {
      if (error.response?.data?.missing_fields) {
        const missingFieldsInfo = error.response.data.missing_fields
          .map((field: any) => `Invoice ${field.id}: ${field.message}`)
          .join(', ');
        showError('Lock Failed', `Missing required fields - ${missingFieldsInfo}. Note: PNC Remarks are optional.`);
      } else {
        showError('Lock Failed', 'Failed to lock invoices. Required fields: Exceptions and Invoice Value (PNC Remarks are optional).');
      }
    }
  };

  const calculateSalary = (invoice: Invoice): number => {
    if (!invoice.invoice_value || !invoice.month_year) return 0;
    
    const calculation: HonorariumCalculation = {
      attended_days: invoice.attended_days,
      exceptions: invoice.exceptions || 0,
      base_salary: invoice.invoice_value,
      month_year: invoice.month_year
    };
    
    return invoiceService.calculateHonorarium(calculation).calculated_salary;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Draft':
        return 'badge badge-warning';
      case 'Approved By PnC':
        return 'badge badge-info';
      case 'Approved By BU':
        return 'badge badge-success';
      default:
        return 'badge';
    }
  };

  const toggleInvoiceSelection = (invoiceId: number) => {
    setSelectedInvoices(prev => 
      prev.includes(invoiceId) 
        ? prev.filter(id => id !== invoiceId)
        : [...prev, invoiceId]
    );
  };

  const toggleSelectAll = () => {
    if (!invoicesData?.list) return;
    
    const draftInvoices = invoicesData.list.filter(inv => inv.state === 'Draft');
    
    if (selectedInvoices.length === draftInvoices.length) {
      setSelectedInvoices([]);
    } else {
      setSelectedInvoices(draftInvoices.map(inv => inv.id));
    }
  };

  // Loading and error states
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading invoices...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-red-600">Error loading invoices. Please try again.</div>
      </div>
    );
  }

  const invoices = invoicesData?.list || [];
  const draftInvoices = invoices.filter(inv => inv.state === 'Draft');
  const canLockSelected = selectedInvoices.length > 0 && selectedInvoices.every(id => 
    draftInvoices.find(inv => inv.id === id)
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl font-bold leading-7 text-rgf-gray-900 sm:text-3xl">
            PnC Review - Step 1
          </h1>
          <p className="mt-1 text-sm text-rgf-gray-500">
            Review attendance, update exceptions and invoice values, then approve for BU review.
          </p>
        </div>
        <div className="mt-4 flex md:mt-0 md:ml-4 space-x-3">
          <Button
            variant="secondary"
            icon={<Upload className="h-4 w-4" />}
            onClick={() => setShowSalaryImport(true)}
          >
            Import Monthly Amount Data
          </Button>
          <Button
            variant="secondary"
            icon={<Upload className="h-4 w-4" />}
            onClick={() => setShowExceptionsImport(true)}
          >
            Import Exceptions
          </Button>
          <Button
            variant="secondary"
            icon={<Download className="h-4 w-4" />}
            onClick={handleExportData}
          >
            Export
          </Button>
          {isBulkEditMode ? (
            <>
              <Button
                icon={<Save className="h-4 w-4" />}
                onClick={saveAllChanges}
                disabled={isBulkSaving}
              >
                {isBulkSaving ? 'Saving...' : `Save All (${bulkEditMap.size})`}
              </Button>
              <Button
                variant="secondary"
                icon={<X className="h-4 w-4" />}
                onClick={cancelAllEditing}
                disabled={isBulkSaving}
              >
                Cancel All
              </Button>
            </>
          ) : (
            <>
              {selectedInvoices.length > 0 && (
                <Button
                  variant="secondary"
                  icon={<Edit2 className="h-4 w-4" />}
                  onClick={startBulkEditing}
                >
                  Edit Selected ({Math.min(selectedInvoices.length, BULK_EDIT_LIMIT)}{selectedInvoices.length > BULK_EDIT_LIMIT ? ' max' : ''})
                </Button>
              )}
              {canLockSelected && (
                <Button
                  icon={<CheckCircle className="h-4 w-4" />}
                  onClick={() => lockInvoices(selectedInvoices)}
                >
                  Approve Selected ({selectedInvoices.length})
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Filters and Search */}
      <div className="card">
        <div className="card-body">
          <div className="space-y-4">
            {/* Search Bar */}
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <Input
                  placeholder="Search by name, email, or employee ID..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  startIcon={<Search className="h-4 w-4" />}
                />
              </div>
              <Button 
                variant="secondary" 
                icon={<Filter className="h-4 w-4" />}
                onClick={() => setShowFilters(!showFilters)}
              >
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </Button>
            </div>

            {/* Collapsible Filters */}
            {showFilters && (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 pt-4 border-t border-rgf-gray-200">
                {/* Month Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Month
                  </label>
                  <input
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  />
                </div>

                {/* Program Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Program
                  </label>
                  <select
                    value={selectedProgram}
                    onChange={(e) => { setSelectedProgram(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {programs.map((program) => (
                      <option key={program.value} value={program.value}>
                        {program.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Role Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    Role
                  </label>
                  <select
                    value={selectedRole}
                    onChange={(e) => { setSelectedRole(e.target.value); setCurrentPage(1); }}
                    disabled={rolesLoading}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500 disabled:opacity-50"
                  >
                    {rolesLoading ? (
                      <option value="">Loading roles...</option>
                    ) : (
                      roleOptions.map((role, index) => (
                        <option key={`${role.value}-${index}`} value={role.value}>
                          {role.label}
                        </option>
                      ))
                    )}
                  </select>
                </div>

                {/* State Filter */}
                <div>
                  <label className="block text-sm font-medium text-rgf-gray-700 mb-2">
                    State
                  </label>
                  <select
                    value={selectedState}
                    onChange={(e) => { setSelectedState(e.target.value); setCurrentPage(1); }}
                    className="w-full px-3 py-2 border border-rgf-gray-300 rounded-lg focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {stateOptions.map((state) => (
                      <option key={state.value} value={state.value}>
                        {state.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Clear Filters */}
                <div className="flex items-end">
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setSelectedMonth(getCurrentMonth());
                      setSelectedProgram('');
                      setSelectedRole('');
                      setSelectedState('');
                      setSearchQuery('');
                      setCurrentPage(1);
                      setPageSize(10);
                    }}
                    className="w-full"
                  >
                    Clear All
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Import Salary Data Modal */}
      {showSalaryImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Import Monthly Amount Data</h3>
              <button
                onClick={() => setShowSalaryImport(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  Upload a CSV file with two columns: <strong>Employee ID</strong> and <strong>Monthly Amount</strong>
                </p>
                <div className="bg-gray-50 p-3 rounded-md text-sm">
                  <div className="font-medium mb-1">Expected CSV format:</div>
                  <div className="font-mono text-xs">
                    Employee ID,Monthly Amount<br/>
                    EMP001,50000<br/>
                    EMP002,45000
                  </div>
                </div>
              </div>
              
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleFileUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {importedSalaryData.size > 0 && (
                <div className="bg-green-50 p-3 rounded-md">
                  <p className="text-sm text-green-800">
                    ✓ {importedSalaryData.size} monthly amount records imported and ready to use.
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => setShowSalaryImport(false)}
              >
                Close
              </Button>
              {importedSalaryData.size > 0 && (
                <Button
                  onClick={applySalaryData}
                >
                  Apply Data
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import Exceptions Data Modal */}
      {showExceptionsImport && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Import Exceptions Data</h3>
              <button
                onClick={() => setShowExceptionsImport(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-600 mb-3">
                  Upload a CSV file with two columns: <strong>Employee ID</strong> and <strong>Exceptions</strong>
                </p>
                <div className="bg-gray-50 p-3 rounded-md text-sm">
                  <div className="font-medium mb-1">Expected CSV format:</div>
                  <div className="font-mono text-xs">
                    Employee ID,Exceptions<br/>
                    EMP001,2<br/>
                    EMP002,0<br/>
                    EMP003,1
                  </div>
                </div>
              </div>
              
              <div>
                <input
                  ref={exceptionsFileInputRef}
                  type="file"
                  accept=".csv"
                  onChange={handleExceptionsFileUpload}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              
              {importedExceptionsData.size > 0 && (
                <div className="bg-green-50 p-3 rounded-md">
                  <p className="text-sm text-green-800">
                    ✓ {importedExceptionsData.size} exceptions records imported and ready to use.
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3 mt-6">
              <Button
                variant="secondary"
                onClick={() => setShowExceptionsImport(false)}
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Banner */}
      {isBulkEditMode && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Edit2 className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-800">
                Bulk Edit Mode: {bulkEditMap.size} of {BULK_EDIT_LIMIT} rows
              </span>
              {saveErrors.size > 0 && (
                <span className="text-sm text-red-600 font-medium">
                  ({saveErrors.size} error{saveErrors.size > 1 ? 's' : ''})
                </span>
              )}
            </div>
            <span className="text-xs text-blue-600">
              Click "Edit" on Draft rows to add more (max {BULK_EDIT_LIMIT})
            </span>
          </div>
        </div>
      )}

      {/* Invoice Table */}
      <div className="card">
        <div className="card-body p-0">
          {invoices.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-rgf-gray-600">No invoices found for the selected criteria.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-rgf-gray-200">
                <thead className="bg-rgf-gray-50">
                  <tr>
                    <th className="table-header w-12">
                      <input
                        type="checkbox"
                        checked={selectedInvoices.length === draftInvoices.length && draftInvoices.length > 0}
                        onChange={toggleSelectAll}
                        className="h-4 w-4 text-rgf-primary-600 focus:ring-rgf-primary-500 border-gray-300 rounded"
                      />
                    </th>
                    <th className="table-header">Associate</th>
                    <th className="table-header">Program</th>
                    <th className="table-header">Attended Days</th>
                    <th className="table-header">Exceptions</th>
                    <th className="table-header">Total Days</th>
                    <th className="table-header">Fixed Monthly Amount</th>
                    <th className="table-header">Calculated Amount</th>
                    <th className="table-header">PnC Remarks</th>
                    <th className="table-header">Status</th>
                    <th className="table-header">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-rgf-gray-200">
                  {invoices.map((invoice) => {
                    const isEditing = bulkEditMap.has(invoice.id);
                    const rowEditValues = bulkEditMap.get(invoice.id);
                    const isRowSaving = savingIds.has(invoice.id);
                    const rowError = saveErrors.get(invoice.id);
                    const canEdit = invoice.state === 'Draft';
                    const hasImportedAmount = importedSalaryData.has(invoice.employee_id);
                    const hasImportedExceptions = importedExceptionsData.has(invoice.employee_id);

                    return (
                      <tr key={invoice.id} className={`hover:bg-rgf-gray-50 ${rowError ? 'bg-red-50' : isEditing ? 'bg-yellow-50' : hasImportedAmount || hasImportedExceptions ? 'bg-blue-50' : ''}`}>
                        <td className="table-cell">
                          {canEdit && (
                            <input
                              type="checkbox"
                              checked={selectedInvoices.includes(invoice.id)}
                              onChange={() => toggleInvoiceSelection(invoice.id)}
                              className="h-4 w-4 text-rgf-primary-600 focus:ring-rgf-primary-500 border-gray-300 rounded"
                            />
                          )}
                        </td>
                        <td className="table-cell">
                          <div className="flex items-center">
                            <div className="h-10 w-10 bg-rgf-primary-100 rounded-full flex items-center justify-center">
                              <User className="h-5 w-5 text-rgf-primary-600" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-rgf-gray-900">
                                {invoice.name}
                              </div>
                              <div className="text-sm text-rgf-gray-500">
                                {invoice.employee_id} • {invoice.designation}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="table-cell">
                          <span className="badge badge-info">{invoice.program}</span>
                        </td>
                        <td className="table-cell">
                          <div className="text-sm text-rgf-gray-900">
                            {invoice.attended_days} days
                          </div>
                        </td>
                        <td className="table-cell">
                          {isEditing && rowEditValues ? (
                            <input
                              type="number"
                              value={rowEditValues.exceptions}
                              onChange={(e) => updateEditValues(invoice.id, 'exceptions', e.target.value)}
                              className={`w-20 px-2 py-1 border rounded text-sm ${rowError ? 'border-red-400' : 'border-gray-300'}`}
                              min="0"
                              placeholder="0"
                              disabled={isRowSaving}
                            />
                          ) : (
                            <div className="text-sm text-rgf-gray-900">
                              <div className="flex items-center space-x-2">
                                <span>{invoice.exceptions || 0} days</span>
                                {hasImportedExceptions && (
                                  <div className="flex items-center space-x-1">
                                    <Upload className="h-3 w-3 text-blue-600" />
                                    <span className="text-xs text-blue-600 font-medium">
                                      {importedExceptionsData.get(invoice.employee_id)} imported
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          <div className="text-sm font-medium text-rgf-gray-900">
                            {invoice.attended_days + (invoice.exceptions || 0)} days
                          </div>
                        </td>
                        <td className="table-cell">
                          {isEditing && rowEditValues ? (
                            <input
                              type="number"
                              value={rowEditValues.fixedMonthlyAmount}
                              onChange={(e) => updateEditValues(invoice.id, 'fixedMonthlyAmount', e.target.value)}
                              className={`w-28 px-2 py-1 border rounded text-sm ${rowError ? 'border-red-400' : 'border-gray-300'}`}
                              placeholder="Enter value"
                              disabled={isRowSaving}
                            />
                          ) : (
                            <div className="text-sm text-rgf-gray-900">
                              <div className="flex items-center space-x-2">
                                <span>-</span>
                                {hasImportedAmount && (
                                  <div className="flex items-center space-x-1">
                                    <Upload className="h-3 w-3 text-blue-600" />
                                    <span className="text-xs text-blue-600 font-medium">
                                      ₹{importedSalaryData.get(invoice.employee_id)?.toLocaleString()}
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          <div className="text-sm font-medium text-rgf-gray-900">
                            {invoice.invoice_value ? `₹${invoice.invoice_value.toLocaleString()}` : '-'}
                          </div>
                        </td>
                        <td className="table-cell">
                          {isEditing && rowEditValues ? (
                            <input
                              type="text"
                              value={rowEditValues.remarks}
                              onChange={(e) => updateEditValues(invoice.id, 'remarks', e.target.value)}
                              className={`w-32 px-2 py-1 border rounded text-sm ${rowError ? 'border-red-400' : 'border-gray-300'}`}
                              placeholder="Add remarks"
                              disabled={isRowSaving}
                            />
                          ) : (
                            <div className="text-sm text-rgf-gray-500">
                              {invoice.pnc_remarks || '-'}
                            </div>
                          )}
                        </td>
                        <td className="table-cell">
                          <span className={getStatusBadgeClass(invoice.state || 'Draft')}>
                            {invoice.state || 'Draft'}
                          </span>
                        </td>
                        <td className="table-cell">
                          <div className="flex flex-col">
                            <div className="flex items-center space-x-2">
                              {isEditing ? (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => saveRow(invoice.id).then(ok => { if (ok) { showSuccess('Saved', `${invoice.name} updated.`); refetch(); } })}
                                    disabled={isRowSaving || isBulkSaving}
                                    className="text-green-600 hover:text-green-800"
                                    icon={<Save className="h-3 w-3" />}
                                  >
                                    {isRowSaving ? '...' : 'Save'}
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => cancelRowEditing(invoice.id)}
                                    disabled={isRowSaving}
                                    className="text-gray-600 hover:text-gray-800"
                                    icon={<X className="h-3 w-3" />}
                                  >
                                    Cancel
                                  </Button>
                                </>
                              ) : canEdit ? (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => startEditing(invoice)}
                                    disabled={!canAddMoreEdits}
                                    className="text-blue-600 hover:text-blue-800"
                                    icon={<Edit2 className="h-3 w-3" />}
                                  >
                                    Edit
                                  </Button>
                                  {!isBulkEditMode && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => lockInvoices([invoice.id])}
                                      className="text-green-600 hover:text-green-800"
                                      icon={<CheckCircle className="h-3 w-3" />}
                                    >
                                      Approve
                                    </Button>
                                  )}
                                </>
                              ) : (
                                <span className="text-sm text-gray-500">
                                  {invoice.state === 'Approved By PnC' && 'Waiting for BU Review'}
                                  {invoice.state === 'Approved By BU' && 'Completed'}
                                </span>
                              )}
                            </div>
                            {rowError && (
                              <span className="text-xs text-red-600 mt-1">{rowError}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Pagination */}
      {invoicesData && invoicesData.list.length > 0 && (
        <div className="card">
          <div className="card-body">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-gray-700">
                  Showing page {invoicesData.page} with {invoicesData.list.length} records
                </div>
                <div className="flex items-center space-x-2">
                  <label className="text-sm text-gray-700">Page size:</label>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                    className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-rgf-primary-500 focus:border-rgf-primary-500"
                  >
                    {pageSizeOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={invoicesData.list.length < pageSize}
                  onClick={() => setCurrentPage(prev => prev + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};